if (!customElements.get('gift-note-trigger')) {
  function ownerFormIdForElement(element) {
    const sectionId = element?.dataset?.section || element?.dataset?.sectionId;
    const productId = element?.dataset?.productId;
    return sectionId && productId ? `Product-Form-${sectionId}${productId}` : null;
  }

  function hasFormReference(element, formId) {
    if (!element || !formId) return false;
    if (element.getAttribute?.('form') === formId) return true;
    return Array.from(element.querySelectorAll?.('[form]') || [])
      .some((control) => control.getAttribute('form') === formId);
  }

  function resolveOwnedElement(scope, selector, ownerFormId) {
    const candidates = Array.from(scope?.querySelectorAll?.(selector) || []);
    if (!candidates.length) return null;

    if (ownerFormId) {
      const owned = candidates.filter((candidate) =>
        ownerFormIdForElement(candidate) === ownerFormId ||
        candidate.id === ownerFormId ||
        hasFormReference(candidate, ownerFormId)
      );
      if (owned.length === 1) return owned[0];
      if (owned.length > 1) return null;

      // If the candidates advertise a different owner, never silently use it.
      const hasOwnershipMetadata = candidates.some((candidate) =>
        ownerFormIdForElement(candidate) ||
        candidate.id ||
        hasFormReference(candidate, ownerFormId)
      );
      if (hasOwnershipMetadata) return null;
    }

    // A single ownerless candidate is a safe compatibility fallback for
    // lightweight embeds and legacy tests. Multiple candidates are ambiguous.
    return candidates.length === 1 ? candidates[0] : null;
  }

  class GiftNoteTrigger extends HTMLElement {
    // -- Properties from data attributes --
    get sectionId() { return this.dataset.sectionId; }
    get productId() { return this.dataset.productId; }
    get productHandle() { return this.dataset.productHandle; }

    // -- Resolve owning product form (same pattern as variant-selects.resolveOwningProductForm) --
    get productFormId() {
      return this.sectionId && this.productId
        ? `Product-Form-${this.sectionId}${this.productId}`
        : null;
    }

    resolveProductForm() {
      // 1. By ID convention (most reliable)
      if (this.productFormId) {
        const form = document.getElementById(this.productFormId);
        if (form?.tagName === 'FORM') return form;
      }
      // 2. Section-scoped fallback
      const section = this.closest('section, .product, [data-section]');
      return resolveOwnedElement(
        section,
        'product-form form[action*="/cart/add"], product-form form',
        this.productFormId
      );
    }

    resolveAnalyticsVariantId() {
      const form = this.resolveProductForm();
      const idField = form?.elements?.namedItem?.('id') || form?.querySelector?.('[name="id"]');
      const formVariantId = Number(idField?.value);
      if (Number.isInteger(formVariantId) && formVariantId > 0) return formVariantId;

      const picker = this.resolveVariantSelect();
      const pickerVariantId = Number(picker?.currentVariant?.id);
      return Number.isInteger(pickerVariantId) && pickerVariantId > 0 ? pickerVariantId : null;
    }

    resolveVariantSelect() {
      const section = this.closest('section, .product, [data-section]');
      return resolveOwnedElement(section, 'variant-selects, variant-radios', this.productFormId);
    }

    hasSelectableGiftVariant(variantSelect) {
      if (!variantSelect?.findGiftWrappedVariant) return false;
      try {
        return !!variantSelect.findGiftWrappedVariant();
      } catch (_) {
        return false;
      }
    }

    ensureGiftVariantSelected() {
      const variantSelect = this.resolveVariantSelect();
      if (!variantSelect?.selectGiftWrappedVariant) {
        return { ok: false, error: 'variant_picker_not_found' };
      }

      return variantSelect.selectGiftWrappedVariant();
    }

    // -- localStorage key uses data-product-handle, NOT window.location.pathname --
    getStorageKey() {
      const handle = this.productHandle;
      return handle
        ? `sc_gift_config_prototype:${handle}`
        : 'sc_gift_config_prototype';
    }

    loadConfig() {
      try {
        const raw = localStorage.getItem(this.getStorageKey());
        return raw ? JSON.parse(raw) : null;
      } catch (_) { return null; }
    }

    // -- Public method for modal to inject config into the correct form --
    injectGiftConfig(config) {
      const form = this.resolveProductForm();
      if (!form) return { ok: false, error: 'form_not_found' };
      const Gift = window.SnowcubesGift;
      if (Gift?.upsertGiftInputs) {
        return Gift.upsertGiftInputs(form, config);
      }
      return { ok: false, error: 'core_not_loaded' };
    }

    // -- Update this card's state from its own section context --
    updateState({ animate = true } = {}) {
      const Gift = window.SnowcubesGift;
      if (!Gift) return;

      const variantSelect = this.resolveVariantSelect();
      const variantTitle = variantSelect?.currentVariant?.title || '';
      const form = this.resolveProductForm();
      const quantityInput = form?.querySelector('input[name="quantity"]');
      const quantity = parseInt(quantityInput?.value, 10) || 1;
      const config = this.loadConfig();
      const isGiftWrapped = Gift.isGiftWrappedVariantTitle
        ? Gift.isGiftWrappedVariantTitle(variantTitle)
        : /\bgift[-\s]*wrap(ped)?\b/i.test(variantTitle || '');
      const hasSelectableGiftVariant = this.hasSelectableGiftVariant(variantSelect);
      const configAppliesToCurrentVariant = isGiftWrapped || !hasSelectableGiftVariant;

      const model = Gift.deriveGiftNoteCardModel({
        giftUIEnabled: true,
        variantTitle,
        quantity,
        config,
        giftAlwaysOn: this.hasAttribute('data-gift-always-on'),
        configAppliesToCurrentVariant,
        productHandle: this.productHandle,
      });

      Gift.applyGiftNoteCardModel(this, model, { animate });
    }

    // -- Lifecycle --
    connectedCallback() {
      this._onClickBound = this._onClick.bind(this);
      this.addEventListener('click', this._onClickBound);

      // Initial state update (deferred to let variant-selects initialize first)
      setTimeout(() => this.updateState({ animate: false }), 0);
    }

    disconnectedCallback() {
      if (this._onClickBound) {
        this.removeEventListener('click', this._onClickBound);
      }
    }

    // -- Click handler: open modal with this trigger as context --
    _onClick(e) {
      const btn = e.target.closest('[data-gift-note-open]');
      if (!btn) return;

      // Stop propagation so the global click handler in gift-box-customizer-modal.js
      // doesn't also fire (that handler is the backward-compat fallback)
      e.stopPropagation();

      const mode = btn.dataset.mode || 'add';
      const options = { mode, trigger: this };
      const analyticsVariantId = this.resolveAnalyticsVariantId();
      if (analyticsVariantId !== null) options.analyticsVariantId = analyticsVariantId;

      if (mode === 'edit') {
        const config = this.loadConfig();
        if (config) {
          options.config = config;
        } else {
          options.mode = 'add';
        }
      }

      window.openGiftBoxModal?.(options);
    }
  }

  customElements.define('gift-note-trigger', GiftNoteTrigger);
}
